<div class="d-flex flex-column h-100">
  <nav class="navbar navbar-expand navbar-main p-0">
    <div class="container-fluid gap-2">
      <a class="navbar-brand d-flex gap-2 align-items-center me-auto" routerLink="/start">
        @if (logoUrl$ | async; as logoUrl) {
          <img [src]="logoUrl" height="38" class="d-inline-block" crossorigin="anonymous" alt="logo" />
        }
        <span>{{ brandName$ | async }}</span>
      </a>
      <span>
        <app-switch-back-button></app-switch-back-button>
      </span>
      <span>
        <app-load-esi-button></app-load-esi-button>
      </span>
      <span>
        <app-update-software-button></app-update-software-button>
      </span>
    </div>
  </nav>

  <div class="flex-grow-1 overflow-auto bg-body-tertiary">
    <div class="container">
      <div class="row gap-3">
        <div class="col-10 offset-1 mt-5" *appIsElectron>
          <div class="p-3">
            @if (showNpcapWarning) {
              <div class="alert alert-warning mt-2 mb-3 shadow-sm">
                <h5>Npcap is not detected on your computer!</h5>

                <p class="mb-2">
                  This application requires
                  <a href="https://npcap.com/" target="_blank" rel="noopener">Npcap</a>
                  to be installed with
                  <strong>“WinPcap API-compatible mode” enabled</strong>; otherwise, the master process will fail to
                  start.
                </p>

                <p class="mb-0">After installing Npcap with the correct options, please restart this application.</p>
              </div>
            }

            @if (isPortInUse) {
              <div class="alert alert-info" role="alert">
                <i class="bi-info-circle-fill me-1"></i>
                The master process is currently running in another instance. You can connect to it.
              </div>
            } @else {
              <form [formGroup]="startForm" (ngSubmit)="onStartSubmit()" class="d-flex flex-column gap-4">
                <fieldset
                  [disabled]="(starting$ | async) || (motionMasterRunning$ | async)"
                  class="d-flex flex-column gap-4"
                >
                  <!-- Fieldbuses -->
                  <div>
                    <label for="fieldBus-control" class="mb-2">Select the fieldbus used by your device</label>
                    <app-radio-button-layout
                      id="fieldBus-control"
                      formControlName="fieldBus"
                      direction="horizontal"
                      class="d-block mb-3"
                    >
                      @for (fieldBus of fieldBusMap | keyvalue; track fieldBus.key) {
                        <ng-template [appRadioButtonLayoutOptionValue]="fieldBus.key">
                          <div class="d-flex flex-column align-items-center gap-2">
                            @if (fieldBus.value.logoUrl) {
                              <img
                                [src]="fieldBus.value.logoUrl"
                                width="100"
                                class="d-inline-block py-2"
                                crossorigin="anonymous"
                                alt="{{ fieldBus.value.title }} logo"
                              />
                            } @else {
                              <strong>{{ fieldBus.value.title }}</strong>
                            }
                          </div>
                        </ng-template>
                      }
                    </app-radio-button-layout>
                  </div>

                  <!-- PDO Mode -->
                  <div>
                    <label for="driver-control" class="mb-2">Use this application to</label>
                    <app-radio-button-layout
                      id="driver-control"
                      formControlName="pdoMode"
                      direction="horizontal"
                      class="d-block mb-2"
                    >
                      <ng-template appRadioButtonLayoutOptionValue="control">
                        <div class="d-flex flex-column gap-1 h-100">
                          <h5>Control devices</h5>
                          <small>
                            Select this mode when no other master is controlling the devices and you want to enable the
                            controller, run the motor, use cyclic modes, or execute motion profiles.
                          </small>
                        </div>
                      </ng-template>
                      <ng-template appRadioButtonLayoutOptionValue="monitor">
                        <div class="d-flex flex-column gap-1 h-100">
                          <h5>Monitor data</h5>
                          <small>
                            Select this mode when you only want to monitor process data such as actual position,
                            velocity, and torque. This mode is typically used when the devices are controlled by another
                            master, for example a PLC via PROFINET or EtherNet/IP.
                          </small>
                        </div>
                      </ng-template>
                    </app-radio-button-layout>
                  </div>

                  <!-- Connect via SPoE checkbox -->
                  @if (isFieldBusEtherCat && isPdoModeControl) {
                    <div class="form-check">
                      <input
                        formControlName="spoeCheckbox"
                        class="form-check-input"
                        type="checkbox"
                        value=""
                        id="useSpoeDriver"
                        aria-describedby="use-spoe-driver-description"
                      />
                      <label class="form-check-label" for="useSpoeDriver">Connect via SPoE</label>
                      <div>
                        <small id="use-spoe-driver-description" class="text-secondary">
                          SOMANET Protocol over Ethernet (SPoE) is a custom non-realtime protocol used for configuring
                          and monitoring SOMANET ACTILINK-S devices.
                        </small>
                      </div>
                    </div>
                  }
                </fieldset>

                <!-- Motion Master connection -->
                <div class="bg-body border border-1 rounded-3 p-4">
                  <fieldset
                    [disabled]="(starting$ | async) || (motionMasterRunning$ | async)"
                    class="d-flex flex-column gap-3"
                  >
                    @if (isSpoeDriver) {
                      <p class="m-0">
                        <strong>Supported devices:</strong> ACTILINK-S (Integro) devices with firmware v5.6.0 or later.
                      </p>
                      <a
                        href="#"
                        class="text-body-secondary small"
                        (click)="$event.preventDefault(); showSpoeInstructions = !showSpoeInstructions"
                      >
                        {{ showSpoeInstructions ? 'Hide instructions' : 'How to communicate with devices via SPoE...' }}
                      </a>
                      @if (showSpoeInstructions) {
                        <div class="text-body-secondary">
                          <p>
                            To communicate with devices via <strong>SOMANET Protocol over Ethernet (SPoE)</strong>, your
                            PC and the servo controller must have IP addresses in the <strong>same subnet</strong>. You
                            can achieve this in several ways:
                          </p>
                          <ol>
                            <li>
                              <strong>Using a DHCP-enabled network:</strong>
                              <ul>
                                <li>
                                  Connect both the PC and the servo controller to a network with a DHCP server, e.g., a
                                  standard router.
                                </li>
                                <li>Determine the IP address assigned to your servo controller.</li>
                              </ul>
                            </li>
                            <li>
                              <strong>Direct connection between PC and servo controller:</strong>
                              <ul>
                                <li>Connect the servo controller directly to your PC's network interface.</li>
                                <li>
                                  Depending on the firmware running on your servo controller, you may need a tool like
                                  <strong>PRONETA</strong> to assign an IP address, or use a third-party DHCP server
                                  such as
                                  <a href="https://www.dhcpserver.de/cms/download/" target="_blank">dhcpserver.de</a>.
                                </li>
                              </ul>
                            </li>
                            <li>
                              <strong>Using a PLC or master controller with DHCP functionality:</strong>
                              <ul>
                                <li>
                                  If the master controller connected to your servo controller has a DHCP feature, it can
                                  assign IP addresses to both the PC and the servo controller when connected to the same
                                  network.
                                </li>
                              </ul>
                            </li>
                          </ol>
                        </div>
                      }
                      <h6>Device IP addresses</h6>
                      <app-form-ipv4 class="d-block" formControlName="ips"></app-form-ipv4>
                    } @else {
                      <div class="form-group">
                        <p><strong>Supported devices:</strong> all SOMANET devices with EtherCAT firmware.</p>
                        <label for="networkInterface">Network interface</label>
                        <select
                          class="form-select"
                          title="Select MAC address"
                          (change)="onChangeNetworkInterface($event)"
                          id="networkInterface"
                        >
                          @for (inf of networkInterfaces; track inf.mac) {
                            <option [value]="inf.mac" [selected]="inf.mac === startForm.get('mac')?.value">
                              {{ inf.ifaceName }} ({{ inf.mac }})
                            </option>
                          }
                        </select>
                        <small class="d-block mt-1 text-body-secondary">
                          The network interface to which the servo drive devices are connected.
                        </small>
                      </div>
                      @if (expertMode$ | async) {
                        <div class="form-group">
                          <label for="macAddress">MAC address</label>
                          <input
                            type="text"
                            class="form-control"
                            formControlName="mac"
                            title="Address"
                            id="macAddress"
                          />
                          <small class="d-block mt-1 text-body-secondary">
                            Choose from the list of network interfaces or enter the MAC address manually.
                          </small>
                        </div>
                      } @else {
                        <input type="hidden" formControlName="mac" />
                      }
                    }
                    <!-- Communication ports -->
                    @if (expertMode$ | async) {
                      <div class="d-flex gap-3">
                        <div class="flex-fill form-group">
                          <label for="req-res-port">REQ/RES port</label>
                          <input
                            type="number"
                            name=""
                            id="req-res-port"
                            class="form-control"
                            formControlName="reqResPort"
                          />
                        </div>
                        <div class="flex-fill form-group">
                          <label for="pub-sub-port">PUB/SUB port</label>
                          <input
                            type="number"
                            name=""
                            id="pub-sub-port"
                            class="form-control"
                            formControlName="pubSubPort"
                          />
                        </div>
                      </div>
                      @if (!isSpoeDriver) {
                        <div class="form-check mt-3">
                          <input
                            formControlName="noOdCache"
                            class="form-check-input"
                            type="checkbox"
                            id="noOdCache"
                            aria-describedby="no-od-cache-description"
                          />
                          <label class="form-check-label" for="noOdCache">Do not use the object dictionary cache</label>
                          <div>
                            <small id="no-od-cache-description" class="text-secondary">
                              The master process reads the object dictionary from every device on each scan and does not
                              save it. Scanning takes longer.
                            </small>
                          </div>
                        </div>
                      }
                    }
                  </fieldset>
                  <div class="d-grid gap-2 mt-3">
                    @if (motionMasterRunning$ | async) {
                      <button
                        class="btn btn-lg btn-outline-secondary w-100"
                        type="button"
                        [disabled]="(connecting$ | async) || (stopping$ | async)"
                        (click)="onStop()"
                      >
                        STOP THE MASTER PROCESS
                      </button>
                    } @else {
                      <button
                        class="btn btn-lg btn-primary w-100"
                        type="submit"
                        [disabled]="startFormInvalid || (starting$ | async)"
                      >
                        {{ (expertMode$ | async) ? 'START THE MASTER PROCESS' : 'START' }}
                      </button>
                    }
                  </div>
                </div>
              </form>
            }
            @if (!isPortInUse) {
              <div class="text-center">
                <button type="button" class="btn btn-link mt-1" (click)="onSaveTheMasterProcessStandardOutput()">
                  SAVE THE MASTER LOG
                </button>
              </div>
            }
            @if (!isPortInUse && canClearOdCache) {
              <div class="text-center">
                <button
                  type="button"
                  class="btn btn-link"
                  aria-describedby="clear-od-cache-description"
                  [disabled]="(motionMasterRunning$ | async) || (clearingOdCache$ | async)"
                  (click)="onClearOdCache()"
                >
                  CLEAR THE OBJECT DICTIONARY CACHE
                </button>
                <div>
                  <small id="clear-od-cache-description" class="text-secondary">
                    Deletes the saved object dictionaries. On the next start, the master process reads them from the
                    devices again. Use it after a firmware update when parameters are missing or wrong. Available while
                    the master process is stopped.
                  </small>
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Connection screen -->
        @if (!isElectronApp || isPortInUse || (expertMode$ | async)) {
          <div class="col-10 offset-1">
            <div class="p-3">
              <form
                [formGroup]="connectForm"
                (ngSubmit)="onConnectSubmit()"
                class="bg-body border border-1 rounded-3 p-4"
              >
                <fieldset [disabled]="connecting$ | async">
                  <div class="row ga-3">
                    <div class="col-12">
                      <div class="row">
                        <div class="form-group" [class.col-md-6]="expertMode$ | async">
                          <label for="hostname2">Hostname</label>
                          <div class="input-group">
                            <span class="input-group-text">ws://</span>
                            <input
                              type="text"
                              class="form-control"
                              id="hostname2"
                              name="hostname2"
                              formControlName="hostname"
                            />
                          </div>
                          <small class="d-block mt-1 text-body-secondary">
                            The host IP address or domain name. Examples include: 192.168.1.112 or
                            oblac-drives-2ef2360f.local
                          </small>
                        </div>

                        @if (expertMode$ | async) {
                          <div class="form-group col-md-6">
                            <label for="clientId">Client ID</label>
                            <input
                              type="text"
                              class="form-control"
                              id="clientId"
                              name="clientId"
                              formControlName="clientId"
                            />
                            <small class="d-block mt-1 text-body-secondary">
                              Every client connecting to the system must provide a unique ID. The ID will be appended to
                              the URL as a clientId query parameter.
                            </small>
                          </div>
                        }
                      </div>
                    </div>
                    @if (expertMode$ | async) {
                      <div class="col-12">
                        <div class="row">
                          <div class="form-group col-md-6">
                            <label for="port1">Port 1 (Req/Res)</label>
                            <input
                              type="number"
                              min="0"
                              max="65353"
                              class="form-control"
                              id="port1"
                              name="port1"
                              formControlName="reqResPort"
                            />
                            <small class="d-block mt-1 text-body-secondary">
                              This port supports non-real-time request/response messaging and a keep-alive mechanism.
                            </small>
                          </div>
                          <div class="form-group col-md-6">
                            <label for="port2">Port 2 (Pub/Sub)</label>
                            <input
                              type="number"
                              min="0"
                              max="65353"
                              class="form-control"
                              id="port2"
                              name="port2"
                              formControlName="pubSubPort"
                            />
                            <small class="d-block mt-1 text-body-secondary">
                              This port is used exclusively for monitoring real-time data. Client requests are not
                              supported.
                            </small>
                          </div>
                        </div>
                      </div>
                      <div class="col-12">
                        <div class="row">
                          <div class="form-group col-md-4">
                            <label for="clientAliveTimeout">Client alive timeout</label>
                            <div class="input-group">
                              <input
                                type="number"
                                min="100"
                                max="4294967295"
                                class="form-control"
                                id="clientAliveTimeout"
                                name="clientAliveTimeout"
                                formControlName="clientAliveTimeout"
                              />
                              <span class="input-group-text">ms</span>
                            </div>
                            <small class="d-block mt-1 text-body-secondary">
                              The time system considers the client alive without receiving a message from it.
                            </small>
                          </div>
                          <div class="form-group col-md-4">
                            <label for="systemAliveTimeout">System alive timeout</label>
                            <div class="input-group">
                              <input
                                type="number"
                                min="100"
                                max="60000"
                                class="form-control"
                                id="systemAliveTimeout"
                                name="systemAliveTimeout"
                                formControlName="systemAliveTimeout"
                              />
                              <span class="input-group-text">ms</span>
                            </div>
                            <small class="d-block mt-1 text-body-secondary">
                              The time client considers the system alive without receiving a message from it.
                            </small>
                          </div>
                          <div class="form-group col-md-4">
                            <label for="pingSystemInterval">Ping system interval</label>
                            <div class="input-group">
                              <input
                                type="number"
                                min="50"
                                max="60000"
                                class="form-control"
                                id="pingSystemInterval"
                                name="pingSystemInterval"
                                formControlName="pingSystemInterval"
                              />
                              <span class="input-group-text">ms</span>
                            </div>
                            <small class="d-block mt-1 text-body-secondary">
                              The time intervals in which the client will send the ping messages to the system.
                            </small>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                  <ng-container *ngTemplateOutlet="connectSubmitButton; context: { form: connectForm }"></ng-container>
                </fieldset>
              </form>

              <ng-template #connectSubmitButton let-form="form">
                @if (connecting$ | async) {
                  <div class="progress mt-3" style="height: 3rem; opacity: 0.25">
                    <div
                      class="btn btn-primary progress-bar progress-bar-striped progress-bar-animated w-100"
                      role="progressbar"
                      aria-valuenow="100"
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      CONNECTING
                    </div>
                  </div>
                } @else {
                  <div class="d-grid mt-3">
                    <button class="btn btn-primary w-100" style="height: 3rem" type="submit" [disabled]="form.invalid">
                      CONNECT
                    </button>
                  </div>
                }
              </ng-template>
            </div>
          </div>
        }

        <div class="col-10 offset-1 mb-2">
          <ul class="list-unstyled p-3 ms-4">
            <li style="font-size: 0.75rem" class="text-body-tertiary text-sm"><strong>TRADEMARKS</strong></li>
            @for (fieldBus of fieldBusMap | keyvalue; track fieldBus.key) {
              <li style="font-size: 0.75rem" class="text-body-tertiary text-sm">{{ fieldBus.value.description }}</li>
            }
          </ul>
        </div>
      </div>
    </div>
  </div>

  <app-main-statusbar></app-main-statusbar>
</div>
